import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollText, RefreshCw, LogIn } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLORS = {
  'auth.login': 'bg-green-50 text-green-700 border-green-200',
  'auth.login_failed': 'bg-orange-50 text-orange-800 border-orange-200',
  'auth.lockout': 'bg-red-50 text-red-700 border-red-200',
  'auth.change_password': 'bg-blue-50 text-blue-700 border-blue-200',
  'user.create': 'bg-purple-50 text-purple-700 border-purple-200',
  'user.update': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'user.reset_password': 'bg-blue-50 text-blue-700 border-blue-200',
  'user.deactivate': 'bg-red-50 text-red-700 border-red-200',
  'user.activate': 'bg-green-50 text-green-700 border-green-200',
  'user.invite': 'bg-yellow-50 text-yellow-800 border-yellow-200',
};

const AdminAuditLogPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({ actions: [], entity_types: [] });
  const [filters, setFilters] = useState({
    action: 'all', entity_type: 'all', date_from: '', date_to: '',
  });
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const LIMIT = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip };
      if (filters.action !== 'all') params.action = filters.action;
      if (filters.entity_type !== 'all') params.entity_type = filters.entity_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await axios.get(`${API_URL}/api/admin/audit-logs`, { params });
      setLogs(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters, skip]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/audit-logs/actions`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  if (user && user.role !== 'super_admin') {
    return <div className="p-8 text-center text-muted-foreground">Super Admin access required</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ScrollText className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-heading font-bold">Audit Log</h1>
          <p className="text-sm text-muted-foreground">Track every sensitive action across the system.</p>
        </div>
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all" data-testid="audit-tab-all"><ScrollText className="h-4 w-4 mr-1.5" /> All Activity</TabsTrigger>
          <TabsTrigger value="login" data-testid="audit-tab-login"><LogIn className="h-4 w-4 mr-1.5" /> Login History</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-6 pt-4">
          <AllActivityView
            filters={filters} setFilters={setFilters}
            meta={meta} skip={skip} setSkip={setSkip} fetchLogs={fetchLogs}
            logs={logs} loading={loading} total={total} LIMIT={LIMIT}
            setDetail={setDetail}
          />
        </TabsContent>
        <TabsContent value="login" className="pt-4">
          <LoginHistoryView />
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit entry detail</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground">Action:</span> <strong>{detail.action}</strong></div>
                <div><span className="text-muted-foreground">Timestamp:</span> {new Date(detail.ts).toLocaleString()}</div>
                <div><span className="text-muted-foreground">User:</span> {detail.user_email}</div>
                <div><span className="text-muted-foreground">Role:</span> {detail.user_role}</div>
                <div><span className="text-muted-foreground">Entity:</span> {detail.entity_type} / {detail.entity_id}</div>
                <div><span className="text-muted-foreground">IP:</span> {detail.ip || '—'}</div>
              </div>
              {detail.before && (
                <div>
                  <div className="font-semibold text-xs uppercase text-muted-foreground mt-2">Before</div>
                  <pre className="bg-muted text-xs p-2 rounded overflow-x-auto">{JSON.stringify(detail.before, null, 2)}</pre>
                </div>
              )}
              {detail.after && (
                <div>
                  <div className="font-semibold text-xs uppercase text-muted-foreground mt-2">After</div>
                  <pre className="bg-muted text-xs p-2 rounded overflow-x-auto">{JSON.stringify(detail.after, null, 2)}</pre>
                </div>
              )}
              {detail.user_agent && (
                <div className="text-xs text-muted-foreground"><span>User-Agent:</span> {detail.user_agent}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// All Activity sub-view (extracted from inline JSX so we can sit Login History next to it)
// ---------------------------------------------------------------------------
const AllActivityView = ({ filters, setFilters, meta, skip, setSkip, fetchLogs, logs, loading, total, LIMIT, setDetail }) => (
  <>
    <Card>
      <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <Label className="text-xs">Action</Label>
          <Select value={filters.action} onValueChange={(v) => { setFilters({ ...filters, action: v }); setSkip(0); }}>
            <SelectTrigger data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {meta.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Entity type</Label>
          <Select value={filters.entity_type} onValueChange={(v) => { setFilters({ ...filters, entity_type: v }); setSkip(0); }}>
            <SelectTrigger data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              {meta.entity_types.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">From date</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => { setFilters({ ...filters, date_from: e.target.value }); setSkip(0); }} data-testid="audit-from-date" />
        </div>
        <div>
          <Label className="text-xs">To date</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => { setFilters({ ...filters, date_to: e.target.value }); setSkip(0); }} data-testid="audit-to-date" />
        </div>
        <div className="flex items-end">
          <Button onClick={fetchLogs} variant="outline" className="w-full" data-testid="audit-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle className="text-base">{total} total entries · showing {logs.length}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>IP</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No entries</TableCell></TableRow>
            ) : logs.map((l) => (
              <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{new Date(l.ts).toLocaleString()}</TableCell>
                <TableCell className="text-sm">
                  <div>{l.user_email || '—'}</div>
                  <div className="text-xs text-muted-foreground">{l.user_role}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={ACTION_COLORS[l.action] || ''}>{l.action}</Badge>
                </TableCell>
                <TableCell className="text-xs">
                  {l.entity_type && <div>{l.entity_type}</div>}
                  {l.entity_id && <div className="text-muted-foreground font-mono">{l.entity_id.slice(0, 8)}…</div>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{l.ip || '—'}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDetail(l)} data-testid={`audit-detail-${l.id}`}>View</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {total > LIMIT && (
          <div className="flex justify-between items-center mt-4 text-sm">
            <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} data-testid="audit-prev-btn">Previous</Button>
            <span className="text-muted-foreground">Page {Math.floor(skip / LIMIT) + 1} of {Math.ceil(total / LIMIT)}</span>
            <Button variant="outline" size="sm" disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)} data-testid="audit-next-btn">Next</Button>
          </div>
        )}
      </CardContent>
    </Card>
  </>
);

// ---------------------------------------------------------------------------
// Login History view — filtered to auth events with IP + UA on display
// ---------------------------------------------------------------------------
const LoginHistoryView = () => {
  const [rows, setRows] = useState([]);
  const [emailFilter, setEmailFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (emailFilter.trim()) params.email = emailFilter.trim();
      const r = await axios.get(`${API_URL}/api/admin/audit-logs/login-history`, { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load login history');
    } finally {
      setLoading(false);
    }
  }, [emailFilter]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recent authentication events</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-2 max-w-md">
          <div className="flex-1">
            <Label className="text-xs">Filter by email (partial)</Label>
            <Input placeholder="e.g. @suubi.org" value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} data-testid="login-history-email-filter" />
          </div>
          <Button variant="outline" onClick={fetchHistory} data-testid="login-history-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>User-Agent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No login activity yet</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} data-testid={`login-history-row-${r.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{new Date(r.ts || r.created_at).toLocaleString()}</TableCell>
                <TableCell className="text-sm">{r.user_email || r.actor_email || '—'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={ACTION_COLORS[r.action] || ''}>{r.action}</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.ip || '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate" title={r.user_agent || ''}>{r.user_agent || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default AdminAuditLogPage;
